/**
 * Labstep
 *
 * @module grid/SmartTable/coldefs/metadata/numeric
 * @desc Column Definition for metadata numeric
 */

import type {
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import type { ResourceItemData } from 'labstep-web/components/ResourceItem/Action/BulkCreate/Modal/type';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type { CellRendererParams } from 'labstep-web/core/DataGrid/types';
import { Icon } from 'labstep-web/core/Icon';
import { GridBatchCreateResourceItemsService } from 'labstep-web/grid/BatchCreateResourceItems/services/grid-batch-create-resource-items-service';
import type { BatchCreateResourceItemsColDef } from 'labstep-web/grid/BatchCreateResourceItems/types';
import type { Entity } from 'labstep-web/models/entity.model';
import type { Metadata } from 'labstep-web/models/metadata';
import { MetadataType } from 'labstep-web/models/metadata/types';

export const getMetadataNumericColDef = (
  metadata: Metadata,
  data: ResourceItemData[],
  setData: React.Dispatch<React.SetStateAction<ResourceItemData[]>>,
): BatchCreateResourceItemsColDef => {
  const colId = `metadata:${MetadataType.numeric}:${metadata.guid}`;
  return {
    colId,
    headerName: metadata.label || '',
    isRequired: metadata.is_required ?? false,
    headerComponentParams: {
      icon: ICONS.metadata.type.numeric,
      cornerIcon: ICONS.resource_item.primary,
    },
    editable: true,
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node?.data[colId]) {
        return '';
      }

      const metadataNumeric = params.node.data[colId];

      if (
        metadataNumeric?.number !== undefined &&
        metadataNumeric?.number !== null
      ) {
        return `${metadataNumeric.number} ${metadataNumeric.unit || ''}`;
      }

      return '';
    },
    valueSetter: (params: ValueSetterParams) => {
      if (!params.node) {
        return false;
      }

      const newValue = params.newValue || '';

      let amount = null;
      let unit = null;

      if (typeof newValue === 'string') {
        const regex = /(\d+(?:[.,]\d+)?)(\s*[a-zA-Zµ°±²³´¹ΩΔ\/]*)/;
        const match = newValue.replace(',', '.').match(regex);
        if (!match) {
          GridBatchCreateResourceItemsService.insertErrorInData(
            data,
            setData,
            params,
            `Invalid amount format. Please use the format "number unit" or "number" (e.g. "10 kg" or "10").`,
          );
          return false;
        }
        amount = match[1] !== null ? Number(match[1]) : null;
        unit = match[2].trim();
      } else if (typeof newValue === 'number') {
        amount = newValue;
      }

      const row = params.node.data;

      if (row[colId] === null || row[colId] === undefined) {
        row[colId] =
          GridBatchCreateResourceItemsService.getMetadataBody(
            {
              number: amount,
              unit: unit,
            },
            metadata,
          );
      } else if (amount == null) {
        row[colId] = null;
      } else {
        row[colId] =
          GridBatchCreateResourceItemsService.getMetadataBody(
            {
              number: amount,
              unit: unit && unit.length > 0 ? unit : undefined,
            },
            metadata,
          );
      }

      data[params.node.rowIndex!] = row;
      setData(data);
      GridBatchCreateResourceItemsService.removeErrorInData(
        data,
        setData,
        params,
      );
      return true;
    },
    cellStyle: (params) => {
      if (!params.node) {
        return null;
      }

      const err = GridBatchCreateResourceItemsService.findErrorInData(
        data,
        params,
      );
      if (err) {
        return { backgroundColor: '#F5B1B1' };
      }

      return null;
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const err = GridBatchCreateResourceItemsService.findErrorInData(
        data,
        params,
      );

      const metadata = params.node.data[colId];

      if (
        metadata?.number !== undefined &&
        metadata?.number !== null
      ) {
        return (
          <span
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span>{`${metadata.number} ${metadata.unit || ''}`}</span>
            {err && (
              <Icon
                color={'red'}
                name="warning sign"
                popup={{
                  content: err.message,
                }}
              />
            )}
          </span>
        );
      }

      return (
        <span
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <DataGridPlaceholder
            params={params}
            editable={true}
            placeholder={'Enter'}
          />
          {err && (
            <Icon
              color={'red'}
              name="warning sign"
              popup={{
                content: err.message,
              }}
            />
          )}
        </span>
      );
    },
    suppressKeyboardEvent: suppressKeyboardEventClearCells,
  };
};
